import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { ProfileTrafficCard, type ProfileTrafficSummary } from "@/components/ProfileTrafficCard";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, ClipboardCheck, Copy, LockKeyhole } from "lucide-react";
import { useParams } from "react-router-dom";
import { FocusShell } from "@/components/layout/FocusShell";
import { Button, ButtonLink, Notice, Panel, SelectField, Status, TextAreaField, TextField } from "@/components/system";
import { useCity } from "@/contexts/CityContext";
import {
  ACCEPTING_LABELS,
  REFERRAL_METHOD_OPTIONS,
  WAIT_BUCKET_OPTIONS,
  acceptingLabel,
  referralMethodLabel,
  waitBucketLabel,
  type AcceptingStatus,
  type Availability,
  type PracticeGroupSummary,
  type ReferralMethod,
  type WaitBucket,
} from "@/lib/availability";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { SEO } from "@/lib/seo";

type ContactRole = "practice_manager" | "specialist";

interface ClaimFacility {
  id: string;
  slug: string;
  name: string;
  specialty: string | null;
  suburb: string | null;
  availability: Availability | null;
}

interface ClaimPeekResponse {
  kind: "claim";
  group: PracticeGroupSummary | null;
  facilities: ClaimFacility[];
  contact: { email: string };
  expiresAt: string;
  /** Absent from APIs that predate migration 0028. */
  traffic?: ProfileTrafficSummary;
}

interface ClaimEntryState {
  facilityId: string;
  accepting: AcceptingStatus | null;
  waitBucket: WaitBucket | null;
  referralMethod: ReferralMethod | null;
  referralEmail: string;
  referralFax: string;
  referralNotes: string;
  telehealth: boolean;
  bulkBill: boolean;
}

interface ContactState {
  name: string;
  role: ContactRole;
  email: string;
  phone: string;
}

interface ClaimResult {
  ok: true;
  updated: number;
}

const EMPTY_CONTACT: ContactState = {
  name: "",
  role: "practice_manager",
  email: "",
  phone: "",
};

export default function ClaimLink() {
  const { token = "" } = useParams<{ token: string }>();
  const { city } = useCity();
  const trackedOpen = useRef(false);
  const initialized = useRef(false);
  const [contact, setContact] = useState<ContactState>(EMPTY_CONTACT);
  const [entries, setEntries] = useState<ClaimEntryState[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<ClaimResult | null>(null);

  useEffect(() => {
    if (trackedOpen.current) return;
    trackedOpen.current = true;
    track("claim_link_open");
  }, []);

  const claimQuery = useQuery({
    queryKey: ["claim-link", city.slug, token],
    queryFn: () =>
      api.get<ClaimPeekResponse>(`/v1/public/${city.slug}/claim/${token}`, {
        skipAuth: true,
      }),
    enabled: Boolean(token),
    retry: false,
  });

  useEffect(() => {
    if (!claimQuery.data || initialized.current) return;
    initialized.current = true;
    setContact((current) => ({ ...current, email: claimQuery.data.contact.email }));
    setEntries(
      claimQuery.data.facilities.map((facility) => ({
        facilityId: facility.id,
        accepting: facility.availability?.accepting ?? null,
        waitBucket: facility.availability?.waitBucket ?? null,
        referralMethod: facility.availability?.referralMethod ?? null,
        referralEmail: facility.availability?.referralEmail ?? "",
        referralFax: facility.availability?.referralFax ?? "",
        referralNotes: facility.availability?.referralNotes ?? "",
        telehealth: facility.availability?.telehealth ?? false,
        bulkBill: facility.availability?.bulkBill ?? false,
      })),
    );
  }, [claimQuery.data]);

  const submitClaim = useMutation({
    mutationFn: () =>
      api.post<ClaimResult>(
        `/v1/public/${city.slug}/claim/${token}`,
        {
          contact: {
            name: contact.name.trim(),
            role: contact.role,
            email: contact.email.trim(),
            ...(contact.phone.trim() ? { phone: contact.phone.trim() } : {}),
          },
          entries: entries.map((entry) => ({
            facilityId: entry.facilityId,
            accepting: entry.accepting,
            ...(entry.waitBucket ? { waitBucket: entry.waitBucket } : {}),
            ...(entry.referralMethod
              ? { referralMethod: entry.referralMethod }
              : {}),
            ...(entry.referralMethod === "email" && entry.referralEmail.trim()
              ? { referralEmail: entry.referralEmail.trim() }
              : {}),
            ...(entry.referralMethod === "fax" && entry.referralFax.trim()
              ? { referralFax: entry.referralFax.trim() }
              : {}),
            ...(entry.referralNotes.trim()
              ? { referralNotes: entry.referralNotes.trim() }
              : {}),
            telehealth: entry.telehealth,
            bulkBill: entry.bulkBill,
          })),
        },
        { skipAuth: true },
      ),
    onSuccess: (response) => {
      track("claim_submitted", {
        facility_count: entries.length,
        updated_count: response.updated,
      });
      setResult(response);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onError: (error) => setFormError(messageForError(error)),
  });

  const updateEntry = (index: number, patch: Partial<ClaimEntryState>) => {
    setEntries((current) =>
      current.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, ...patch } : entry,
      ),
    );
  };

  const applyFirstToAll = () => {
    const first = entries[0];
    if (!first) return;
    setEntries((current) =>
      current.map((entry, index) =>
        index === 0
          ? entry
          : {
              ...first,
              facilityId: entry.facilityId,
            },
      ),
    );
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    if (!contact.name.trim() || !contact.email.trim()) {
      setFormError("Enter your name and email before submitting.");
      return;
    }
    if (entries.some((entry) => entry.accepting === null)) {
      setFormError("Choose an accepting-referrals status for every doctor.");
      return;
    }

    submitClaim.mutate();
  };

  const title = claimQuery.data?.group?.name ?? "Practice availability";

  return (
    <FocusShell width="wide">
      <SEO
        title={`Update referral availability · ${title}`}
        description="Confirm current specialist referral availability for PulseBoard."
        path="/claim"
        noindex
      />
      <div className="ds-claim">
        {claimQuery.isLoading ? (
          <ClaimMessage title="Opening this practice link…">We are checking which doctors are included.</ClaimMessage>
        ) : claimQuery.isError || !claimQuery.data ? (
          <ClaimMessage title="This link is no longer available">
            It may have expired or already been used. Nothing was changed by opening it. Email hello@pulseboard.com.au
            for a new link.
          </ClaimMessage>
        ) : result ? (
          <ClaimSuccess data={claimQuery.data} entry={entries[0]} updated={result.updated} />
        ) : (
          <>
            <header className="ds-stack" style={{ gap: 12 }}>
              <p className="ds-eyebrow ds-eyebrow--teal">Referral availability</p>
              <h1 className="ds-display-1" style={{ fontSize: "clamp(32px, 4.6vw, 48px)" }}>{title}</h1>
              <p className="ds-lead">
                Confirm what GPs should see for {claimQuery.data.facilities.length}{" "}
                {claimQuery.data.facilities.length === 1 ? "doctor" : "doctors"}. Do not enter patient information.
              </p>
            </header>

            {claimQuery.data.traffic ? (
              <ProfileTrafficCard traffic={claimQuery.data.traffic} profileCount={claimQuery.data.facilities.length} />
            ) : null}

            <form className="ds-stack" style={{ gap: 16 }} onSubmit={handleSubmit} noValidate>
              <Panel aria-labelledby="claim-contact-title">
                <ClaimStepHeading step={1} id="claim-contact-title" title="Your contact details">
                  Used only for confirmation and four-week availability checks.
                </ClaimStepHeading>
                <div className="ds-form-grid">
                  <TextField
                    label="Name"
                    value={contact.name}
                    onChange={(event) => setContact((current) => ({ ...current, name: event.target.value }))}
                    autoComplete="name"
                    required
                  />
                  <SelectField
                    label="Role"
                    value={contact.role}
                    onChange={(event) => setContact((current) => ({ ...current, role: event.target.value as ContactRole }))}
                  >
                    <option value="practice_manager">Practice manager</option>
                    <option value="specialist">Specialist</option>
                  </SelectField>
                  <TextField
                    label="Email"
                    type="email"
                    value={contact.email}
                    onChange={(event) => setContact((current) => ({ ...current, email: event.target.value }))}
                    autoComplete="email"
                    required
                  />
                  <TextField
                    label="Phone"
                    type="tel"
                    value={contact.phone}
                    onChange={(event) => setContact((current) => ({ ...current, phone: event.target.value }))}
                    autoComplete="tel"
                    placeholder="Optional"
                  />
                </div>
              </Panel>

              <section className="ds-stack" style={{ gap: 12 }} aria-labelledby="claim-availability-title">
                <ClaimStepHeading step={2} id="claim-availability-title" title="Doctor availability">
                  Choose the closest current answer for each doctor.
                </ClaimStepHeading>
                {claimQuery.data.facilities.map((facility, index) => {
                  const entry = entries[index];
                  if (!entry) return null;
                  return (
                    <FacilityAvailabilityCard
                      key={facility.id}
                      facility={facility}
                      entry={entry}
                      showApplyToAll={index === 0 && entries.length > 1}
                      onApplyToAll={applyFirstToAll}
                      onChange={(patch) => updateEntry(index, patch)}
                    />
                  );
                })}
              </section>

              {formError ? <Notice tone="error">{formError}</Notice> : null}

              <div className="ds-panel ds-panel--tight ds-claim__submit">
                <p className="ds-small">
                  <LockKeyhole aria-hidden="true" className="ds-inline-icon" /> No patient data. This link is used once,
                  when you submit.
                </p>
                <Button type="submit" size="lg" disabled={submitClaim.isPending}>
                  {submitClaim.isPending ? "Saving…" : "Confirm availability"}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </FocusShell>
  );
}

function ClaimStepHeading({ step, id, title, children }: { step: number; id: string; title: string; children: ReactNode }) {
  return (
    <div className="ds-claim__step">
      <span className="ds-claim__step-num" aria-hidden="true">{step}</span>
      <div className="ds-stack" style={{ gap: 2 }}>
        <h2 id={id} className="ds-title">{title}</h2>
        <p className="ds-small">{children}</p>
      </div>
    </div>
  );
}

function FacilityAvailabilityCard({
  facility,
  entry,
  showApplyToAll,
  onApplyToAll,
  onChange,
}: {
  facility: ClaimFacility;
  entry: ClaimEntryState;
  showApplyToAll: boolean;
  onApplyToAll: () => void;
  onChange: (patch: Partial<ClaimEntryState>) => void;
}) {
  return (
    <Panel as="article">
      <div className="ds-section-head">
        <div>
          <p className="ds-eyebrow">{[facility.specialty, facility.suburb].filter(Boolean).join(" · ")}</p>
          <h3 className="ds-title">{facility.name}</h3>
        </div>
        {showApplyToAll ? (
          <Button size="sm" variant="secondary" onClick={onApplyToAll}>
            <Copy aria-hidden="true" /> Apply to all doctors below
          </Button>
        ) : null}
      </div>

      <fieldset className="ds-fieldset">
        <legend className="ds-field__label">Accepting referrals</legend>
        <div className="ds-cluster">
          {(Object.entries(ACCEPTING_LABELS) as Array<[AcceptingStatus, string]>).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className="ds-chip"
              aria-pressed={entry.accepting === value}
              onClick={() => onChange({ accepting: value })}
            >
              {value === "yes" ? "Accepting" : label}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="ds-fieldset">
        <legend className="ds-field__label">Typical wait</legend>
        <div className="ds-cluster">
          {WAIT_BUCKET_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className="ds-chip"
              aria-pressed={entry.waitBucket === option.value}
              onClick={() => onChange({ waitBucket: option.value })}
            >
              {option.label}
            </button>
          ))}
          {entry.waitBucket ? (
            <Button size="sm" variant="ghost" onClick={() => onChange({ waitBucket: null })}>Clear</Button>
          ) : null}
        </div>
      </fieldset>

      <div className="ds-form-grid">
        <SelectField
          label="How to refer"
          value={entry.referralMethod ?? ""}
          onChange={(event) => onChange({ referralMethod: (event.target.value || null) as ReferralMethod | null })}
        >
          <option value="">Not published</option>
          {REFERRAL_METHOD_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </SelectField>
        {entry.referralMethod === "email" ? (
          <TextField
            label="Referral email"
            type="email"
            value={entry.referralEmail}
            onChange={(event) => onChange({ referralEmail: event.target.value })}
            placeholder="referrals@practice.com.au"
          />
        ) : null}
        {entry.referralMethod === "fax" ? (
          <TextField
            label="Referral fax"
            value={entry.referralFax}
            onChange={(event) => onChange({ referralFax: event.target.value })}
            inputMode="tel"
            placeholder="02 0000 0000"
          />
        ) : null}
        <TextAreaField
          full
          label="What to include (optional)"
          hint={`${entry.referralNotes.length}/600`}
          value={entry.referralNotes}
          onChange={(event) => onChange({ referralNotes: event.target.value })}
          maxLength={600}
          rows={3}
          placeholder="For example: referral letter, recent imaging or pathology"
        />
      </div>

      <div className="ds-cluster" style={{ gap: 20 }}>
        <label className="ds-check">
          <input type="checkbox" checked={entry.telehealth} onChange={(event) => onChange({ telehealth: event.target.checked })} />
          <span>Telehealth available</span>
        </label>
        <label className="ds-check">
          <input type="checkbox" checked={entry.bulkBill} onChange={(event) => onChange({ bulkBill: event.target.checked })} />
          <span>Bulk billing available</span>
        </label>
      </div>
    </Panel>
  );
}

function ClaimSuccess({
  data,
  entry,
  updated,
}: {
  data: ClaimPeekResponse;
  entry: ClaimEntryState | undefined;
  updated: number;
}) {
  const facility = data.facilities[0];
  return (
    <Panel>
      <span className="ds-empty__icon" aria-hidden="true"><ClipboardCheck /></span>
      <div className="ds-stack" style={{ gap: 8 }}>
        <p className="ds-eyebrow ds-eyebrow--teal">Availability saved</p>
        <h1 className="ds-display-2">GPs now see this</h1>
        <p className="ds-body">{updated} {updated === 1 ? "doctor was" : "doctors were"} updated.</p>
      </div>
      {facility && entry?.accepting ? (
        <div className="ds-card" style={{ maxWidth: 420 }}>
          <div className="ds-listing__top">
            <span className="ds-eyebrow">{facility.specialty ?? "Specialist"}</span>
            <Status tone={entry.accepting === "yes" ? "ok" : entry.accepting === "urgent_only" ? "urgent" : "warn"}>{acceptingLabel(entry.accepting)}</Status>
          </div>
          <h2 className="ds-listing__name">{facility.name}</h2>
          <p className="ds-fine">
            {[waitBucketLabel(entry.waitBucket) ? `Typical wait ${waitBucketLabel(entry.waitBucket)}` : null, referralMethodLabel(entry.referralMethod)]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <p className="ds-fine"><Check aria-hidden="true" className="ds-inline-icon" /> Confirmed by practice · today</p>
        </div>
      ) : null}
      <p className="ds-small">We'll check in every four weeks. Confirming again takes one tap.</p>
      <div><ButtonLink to="/directory" variant="secondary">View the directory</ButtonLink></div>
    </Panel>
  );
}

function ClaimMessage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Panel>
      <h1 className="ds-display-2">{title}</h1>
      <p className="ds-body">{children}</p>
    </Panel>
  );
}

function messageForError(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return "We could not save these details. Check the form and try again.";
}
